import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

// Build the absolute URL for a backend-served upload. Same-origin requests are
// fine; the backend serves under REACT_APP_BACKEND_URL via `/api/uploads/...`.
const resolveImage = (img) => {
    if (!img) return "/pills-logo.jpg";
    // images list may contain either a plain string (legacy) or an object
    const candidate = typeof img === "string" ? img : (img.watermarked_url || img.url);
    if (!candidate) return "/pills-logo.jpg";
    if (candidate.startsWith("/api/")) {
        return `${process.env.REACT_APP_BACKEND_URL || ""}${candidate}`;
    }
    return candidate;
};

const ProductCard = ({ product }) => {
    const off = Math.round(((product.mrp - product.selling_price) / product.mrp) * 100);
    const src = resolveImage(product.primary_image_url || (product.images || [])[0]);
    return (
        <Link
            to={`/products/${product.id}`}
            className="group product-card"
            data-testid={`product-card-${product.id}`}
        >
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-50">
                <img
                    src={src}
                    alt={product.name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = "/pills-logo.jpg"; }}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {product.prescription_required && (
                    <span
                        className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md border border-brand-200 bg-brand-50/90 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-700 backdrop-blur"
                        data-testid={`rx-badge-${product.id}`}
                    >
                        <ShieldCheck className="h-3 w-3" />
                        Rx Required
                    </span>
                )}
                {off > 0 && !product.prescription_required && (
                    <span className="absolute right-3 top-3 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                        {off}% off
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-col gap-1 p-4">
                {product.brand ? <p className="label-uppercase">{product.brand}</p> : null}
                <h3 className="line-clamp-2 text-sm font-medium text-zinc-900">{product.name}</h3>
                {product.pack_size && <p className="text-xs text-zinc-500">{product.pack_size}</p>}
                <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-base font-semibold text-zinc-900">₹{product.selling_price}</span>
                    {product.mrp > product.selling_price && (
                        <span className="text-xs text-zinc-400 line-through">₹{product.mrp}</span>
                    )}
                </div>
                {product.price_per_unit ? (
                    <p className="text-[11px] text-zinc-500" data-testid={`per-unit-${product.id}`}>
                        ₹{product.price_per_unit.toFixed(2)} per tablet
                    </p>
                ) : null}
            </div>
        </Link>
    );
};

export { resolveImage };
export default ProductCard;
